import React, { useState } from 'react';
import { Download, X } from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useAppUpdateAvailable } from '@/hooks/useAppUpdateAvailable';
import { isNativeApp } from '@/lib/nativeApp';

// Slim, non-blocking "a new version is available" banner for native installs.
// Bridge until Capgo OTA (CLAUDE.md §24): Android does not permit silent
// installation outside Play Store, so this is a NOTICE plus a manual
// redownload link — never an automatic install.
//
// Dismissal is per-session and per-version: sessionStorage is wiped when the
// app process is killed, so it reappears on next launch, and dismissing build N
// does not hide a later build N+1.
const DISMISS_KEY = 'behtari_update_banner_dismissed';

const readDismissed = (): number | null => {
  try {
    const raw = sessionStorage.getItem(DISMISS_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const UpdateAvailableBanner: React.FC = () => {
  const { t } = useLanguage();
  const { connected } = useNetworkStatus();
  const { updateAvailable, latestVersionCode, apkUrl } = useAppUpdateAvailable();
  const [dismissedCode, setDismissedCode] = useState<number | null>(readDismissed);

  // Offline: the download cannot succeed, and NetworkBanner already occupies
  // this slot — stay out of its way.
  if (!isNativeApp() || !updateAvailable || !connected) return null;
  if (dismissedCode !== null && latestVersionCode !== null && dismissedCode >= latestVersionCode) {
    return null;
  }

  const dismiss = () => {
    try {
      if (latestVersionCode !== null) {
        sessionStorage.setItem(DISMISS_KEY, String(latestVersionCode));
      }
    } catch { /* noop */ }
    setDismissedCode(latestVersionCode);
  };

  const download = () => {
    // Android WebView ignores <a download>; hand the APK URL to the system
    // browser, which is also where the user grants unknown-sources install.
    Browser.open({ url: apkUrl }).catch(() => {
      window.open(apkUrl, '_blank');
    });
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-[9998] flex items-center gap-2 bg-primary px-3 py-2 text-primary-foreground text-sm shadow-md"
    >
      <Download className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1 min-w-0 truncate">{t('update.newVersionAvailable')}</span>
      <button
        type="button"
        onClick={download}
        className="shrink-0 rounded-lg bg-primary-foreground/15 px-3 py-1 font-medium hover:bg-primary-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/60"
      >
        {t('update.downloadNow')}
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('update.laterButton')}
        className="shrink-0 rounded-lg p-1 hover:bg-primary-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground/60"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

export default UpdateAvailableBanner;
